import { ScheduleI } from '@components/sources/source/cloudSource/cloudSourceSchedule/schedules';
import { IBasicSchedule } from '@components/sources/source/cloudSource/cloudSourceSchedule/schedules/basicSchedule';
import { ScheduleTypes } from '@components/sources/source/cloudSource/cloudSourceSchedule/scheduleTypes';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import removeNullValues from '@utils/removeNullValues';
import { CatchErr, FixMe } from '@utils/types';
import { parseExpression } from 'cron-parser';
import { uniq } from 'lodash';
import moment from 'moment';

interface IError {
  code: number;
  message: string;
  reason?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  errorDetails: IError;
  data: T;
}

function getRespData<T>(dataInResp: ApiResponse<T>) {
  if (!dataInResp.success) {
    throw { response: { data: { error: dataInResp.errorDetails } } };
  }
  return dataInResp.data;
}
const convertErrorToHumanReadable = ({ message, reason }: IError): string =>
  message + (reason ? ` : ${reason}` : '');

const getApiErrorMessageDetails = (
  err: CatchErr,
  fallback: string,
): {
  messageWithFallback: string;
  message: string;
} => {
  const message = err?.response?.data?.errorDetails
    ? convertErrorToHumanReadable(err.response.data.errorDetails)
    : 'Unexpected error!';

  return { messageWithFallback: `${fallback} - ${message}`, message };
};

function retrieveErrorMessageOrResponseData<T>(resp: ApiResponse<T>): { errorMessage: string } | T {
  if (!resp.success) {
    return { errorMessage: convertErrorToHumanReadable(resp.errorDetails) };
  }
  return resp.data;
}

const skipSchemaConfig = (defName: string) => defName.toLowerCase() === 's3';

const updateMappings = (
  mappings: { original: string; mapped: string }[],
  columns: string[],
  map_important_overrides: { [i: string]: string },
) => {
  const newMappings = mappings?.reduce((accum: { original: string; mapped: string }[], col) => {
    if (
      col.mapped &&
      (columns.includes(col.original) || Object.keys(map_important_overrides).includes(col.mapped))
    ) {
      accum.push(col);
    }

    return accum;
  }, []);
  return newMappings;
};
const addUserIdInColumns = (
  mappings: { original: string; mapped: string }[],
  columns: string[],
  map_important_overrides: { [i: string]: string },
) => {
  columns = columns || [];
  const columns_to_add = Object.values(map_important_overrides || []).filter(
    (col) => !columns.includes(col),
  );
  columns.push(...columns_to_add);

  const filtered_original_cols = mappings?.reduce((acc: string[], val: FixMe) => {
    if (!acc.includes(val.original)) {
      acc.push(val.original);
    }
    return acc;
  }, []);

  columns = columns.filter((col) => filtered_original_cols.includes(col));
  return uniq(columns);
};

const getConfigDataToUpdate = (config: FixMe) => {
  const {
    accountInfo,
    customResources,
    credentials,
    accessToken,
    refreshToken,
    rudderAccountId,
    ...updatedConfig
  } = config;
  if (config?.mappedToDestination) {
    return config;
  }
  updatedConfig.mappings = updateMappings(
    updatedConfig.mappings,
    updatedConfig.query?.columns || updatedConfig.columns,
    updatedConfig.map_important_overrides,
  );
  const newCols = addUserIdInColumns(
    updatedConfig.mappings,
    updatedConfig.query?.columns || updatedConfig.columns,
    updatedConfig.map_important_overrides,
  );
  if (updatedConfig.query?.columns) {
    updatedConfig.query.columns = newCols;
  } else if (updatedConfig.columns) {
    updatedConfig.columns = newCols;
  }
  delete updatedConfig.map_important_overrides;
  const newResources = (customResources || []).reduce((accum: string[], rsrc: FixMe) => {
    if (!updatedConfig.resources.includes(rsrc.id)) {
      accum.push(rsrc.id);
    }
    return accum;
  }, []);
  const nonNullUpdatedConfig = removeNullValues({
    ...updatedConfig,
    resources: [...updatedConfig.resources, ...newResources],
  });
  return nonNullUpdatedConfig;
};
const generatePreview = (rows: Array<Record<string, unknown>>, columns: IColumn[]) => {
  if (rows.length === 0) {
    return {};
  }
  const preview = rows[0];
  const tableData = columns.reduce((accum: { [name: string]: string }, col) => {
    const currentValue = preview[col.name];
    const isNull = currentValue === null || currentValue === undefined;
    const newAccum = accum;
    newAccum[col.name] = isNull ? 'null' : String(currentValue);
    return accum;
  }, {});
  return tableData;
};
const getStartAtEndAt = (startAt?: string, endAt?: string, t = moment()) => {
  let startAtDate;
  let endAtDate;
  if (startAt && startAt !== '') {
    startAtDate = moment(`${t.format('YYYY')}-${t.format('MM')}-${t.format('DD')} ${startAt}`);
  } else {
    startAtDate = t.clone().startOf('day');
  }
  if (endAt && endAt !== '') {
    endAtDate = moment(`${t.format('YYYY')}-${t.format('MM')}-${t.format('DD')} ${endAt}`);
  } else {
    endAtDate = t.clone().endOf('day');
  }

  return { startAt: startAtDate, endAt: endAtDate };
};

const basicScheduleNextSync = (schedule: IBasicSchedule) => {
  if (!schedule.every) {
    return '';
  }
  const intervalSeconds = schedule.every * 60;
  let { startAt, endAt } = getStartAtEndAt(schedule.start_at, schedule.end_at);
  let scheduleDate = startAt;

  // starting from the start of the day loop until found the first valid time
  while (
    moment().isAfter(scheduleDate) ||
    scheduleDate.isBefore(startAt) ||
    scheduleDate.isAfter(endAt)
  ) {
    if (scheduleDate.isAfter(endAt)) {
      // skip to next day
      scheduleDate = moment().startOf('day').add(1, 'day');

      // recalculate startAt, endAt
      const { startAt: start, endAt: end } = getStartAtEndAt(
        schedule.start_at,
        schedule.end_at,
        scheduleDate,
      );
      startAt = start;
      endAt = end;
    } else {
      scheduleDate = scheduleDate.clone().add(intervalSeconds, 'seconds');
      const { startAt: start, endAt: end } = getStartAtEndAt(
        schedule.start_at,
        schedule.end_at,
        scheduleDate,
      );
      startAt = start;
      endAt = end;
    }
  }
  return scheduleDate.toISOString();
};
const getScheduleDetails = (schedule: ScheduleI) => {
  const { cron_expression, every, type } = schedule;

  const hours = (every || 0) / 60;
  const basicDescription = `Basic: Every ${hours < 1 ? `${every} minutes` : `${hours} hour(s)`}`;
  let subHeaderText;
  let description;
  let nextSchedule;
  switch (type) {
    case ScheduleTypes.Manual:
      subHeaderText = 'Manual';
      description = 'Manual';
      nextSchedule = '';
      break;
    case ScheduleTypes.Cron:
      subHeaderText = 'Cron';
      description = `Cron: ${cron_expression}`;
      try {
        nextSchedule = parseExpression(cron_expression || '', { utc: true })
          .next()
          .toISOString();
      } catch {
        nextSchedule = '';
      }
      break;
    case ScheduleTypes.Basic:
      subHeaderText = 'Basic';
      description = basicDescription;
      nextSchedule = basicScheduleNextSync({ every: 0, unit: 'minutes', ...schedule });
      break;
    default:
      if (every === 0) {
        subHeaderText = 'Manual';
        description = 'Manual';
        nextSchedule = '';
      } else {
        subHeaderText = 'Basic';
        description = basicDescription;
        nextSchedule = basicScheduleNextSync({ every: 0, unit: 'minutes', ...schedule });
      }
  }
  // nextSchedule is in utc time zone
  return { description, subHeaderText, nextSchedule };
};
const convertToHourlyUTC = (t: string, method: 'ceil' | 'floor') => {
  // t is in UTC format
  if (method === 'floor') {
    return moment(t).utc().minutes(0).seconds(0).milliseconds(0).toISOString();
  }

  return moment(t).utc().add(1, 'hours').minutes(0).seconds(0).milliseconds(0).toISOString();
};

export {
  generatePreview,
  getConfigDataToUpdate,
  skipSchemaConfig,
  basicScheduleNextSync,
  getScheduleDetails,
  convertToHourlyUTC,
  getRespData,
  retrieveErrorMessageOrResponseData,
  getApiErrorMessageDetails,
};
